/**
 * Wording shared by the two enrolment flows and the panel that hosts them.
 *
 * These are fallbacks only. Every one of them is handed to `messageFor`, which prefers the
 * server's own `detail`: the API says *which* rule refused, and that is more useful than
 * anything written here.
 */

export const MFA_ERRORS: Record<number, string> = {
  400: 'This account has no email address on file. Contact an administrator.',
  409: 'This account is not fully set up for two-factor authentication. Contact an administrator.',
  502: 'Cannot reach the identity service right now. Please try again shortly.'
};

export const SESSION_EXPIRED = 'Your session has expired. Please sign in again.';

export const TOTP_ALREADY_SET =
  'An authenticator app is already set up for this account. Remove it before setting up a new one.';

/**
 * Both enrolments are deliberately two steps: the first call sends a code or mints a secret and
 * turns **nothing** on, and only the confirm flips the flag. That proves the second factor is
 * really in the user's hands: a reachable mailbox, a working authenticator app. Before the
 * account is locked behind something it cannot produce.
 */
export interface FactorEnrolmentProps {
  /**
   * The factor is confirmed on. `recoveryCodes` is present **only when the account held no
   * unused codes**. Enrolling a second factor must not invalidate the set written down for
   * the first, so the key is simply absent then.
   */
  onEnrolled: (recoveryCodes?: string[]) => void;
  /**
   * The user backed out, or the server says the factor is already on. Either way the parent
   * stops rendering this and re-reads `GET /users/me/mfa`; `reason`, when given, is worth
   * showing next to the control that started it.
   */
  onCancel: (reason?: string) => void;
}
